import createClient from 'openapi-fetch';
import type { paths } from '@/types/api';

const BASE_URL = import.meta.env.VITE_DASHBOARD_API_URL || 'https://dashboard-api.sparkry.ai';

// Initialize auth token from localStorage at module load time
// This prevents race conditions where API calls fire before AuthProvider sets the token
const storedToken = typeof window !== 'undefined'
  ? localStorage.getItem('sparkry_jwt')
  : null;

let authToken: string | null = storedToken;

export const apiClient = createClient<paths>({
  baseUrl: BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Middleware to add auth header to requests
apiClient.use({
  onRequest({ request }) {
    if (authToken) {
      request.headers.set('Authorization', `Bearer ${authToken}`);
    }
    return request;
  },
});

// Global error handler middleware
apiClient.use({
  async onResponse({ response }) {
    // Handle 401 Unauthorized - clear auth and redirect to login
    if (response.status === 401) {
      authToken = null;
      localStorage.removeItem('sparkry_jwt');
      localStorage.removeItem('sparkry_userId');
      localStorage.removeItem('sparkry_email');
      window.location.href = '/login';
      return response;
    }

    // Handle other error responses (4xx, 5xx)
    if (!response.ok) {
      const errorData = await response.clone().json().catch(() => ({
        message: `Request failed with status ${response.status}`,
      }));

      // Log errors for debugging
      console.error('API Error:', {
        status: response.status,
        statusText: response.statusText,
        url: response.url,
        error: errorData,
      });
    }

    return response;
  },
});

export function setAuthToken(token: string | null) {
  authToken = token;
}

export function getAuthToken(): string | null {
  return authToken;
}

export function getBaseUrl(): string {
  return BASE_URL;
}

/**
 * Typed fetch wrapper for endpoints not yet in OpenAPI spec
 * Applies same middleware as apiClient (auth headers, etc.)
 */
export async function typedFetch<T>(
  path: string,
  options?: RequestInit,
): Promise<T> {
  const headers = new Headers(options?.headers);
  headers.set('Content-Type', 'application/json');

  if (authToken) {
    headers.set('Authorization', `Bearer ${authToken}`);
  }

  const response = await fetch(`${BASE_URL}${path}`, {
    ...options,
    headers,
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ message: 'Request failed' }));
    throw new Error(error.message || 'Request failed');
  }

  return response.json() as Promise<T>;
}
